import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, FolderOpen, Download, Trash2 } from 'lucide-react';
import { isElectron } from '../utils/electron';

interface LogMessage {
  message: string;
  level: string;
  timestamp: string;
}

type BadgeVariant = "default" | "destructive" | "outline" | "secondary";

interface DebugLogsProps {
  maxHeight?: string;
}

type LogCallback = (event: any, logData: string) => void;

export function DebugLogs({ maxHeight = "400px" }: DebugLogsProps) {
  const [logs, setLogs] = useState<string[]>([]);
  const [realtimeLogs, setRealtimeLogs] = useState<LogMessage[]>([]);
  const [logFilePath, setLogFilePath] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isElectron()) return;

    // Get initial logs
    window.electronAPI?.getLogs().then((logs) => {
      if (logs) setLogs(logs.split('\n'));
    });

    // Get log file path
    window.electronAPI?.getLogFilePath().then((path) => {
      if (path) setLogFilePath(path);
    });

    // Subscribe to log updates
    const callback: LogCallback = (_, logData) => {
      setLogs((prevLogs) => [...prevLogs, logData]);
    };

    const unsubscribe = window.electronAPI?.onLogMessage(callback);

    // Cleanup
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const handleOpenLogDirectory = () => {
    if (!isElectron()) return;
    window.electronAPI?.openLogDirectory();
  };

  const downloadLogs = () => {
    const blob = new Blob([logs.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hospital-app-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearRealtimeLogs = () => {
    setRealtimeLogs([]);
  };

  const getLevelColor = (level: string): BadgeVariant => {
    switch (level.toUpperCase()) {
      case 'ERROR': return 'destructive';
      case 'WARN': return 'outline';
      case 'INFO': return 'default';
      default: return 'secondary';
    }
  };

  if (!isElectron()) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Debug logs are only available in the desktop app.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Log file: <code className="text-xs">{logFilePath}</code>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenLogDirectory}
          disabled={!isElectron() || !window.electronAPI?.openLogDirectory}
        >
          Open Log Directory
        </Button>
      </div>

      <ScrollArea className="rounded-md border" style={{ maxHeight }}>
        <pre className="p-4 text-xs">
          {logs.map((log, i) => (
            <div key={i} className="font-mono">
              {log}
            </div>
          ))}
        </pre>
      </ScrollArea>

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Debug Logs</h1>
        <div className="flex gap-2">
          <Button 
            onClick={downloadLogs} 
            variant="outline"
            size="sm"
            disabled={!logs.length}
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* Real-time Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Real-time Logs</CardTitle>
          <Button 
            onClick={clearRealtimeLogs}
            variant="outline" 
            size="sm"
            disabled={realtimeLogs.length === 0}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64 w-full border rounded-md p-4">
            {realtimeLogs.length === 0 ? (
              <p className="text-muted-foreground text-sm">No real-time logs yet...</p>
            ) : (
              <div className="space-y-1">
                {realtimeLogs.map((log, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <Badge variant={getLevelColor(log.level)} className="text-xs">
                      {log.level}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <span className="flex-1 font-mono">{log.message}</span>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

export default DebugLogs; 